<template>
  <div role="alert"
       class="pointer-events-auto w-[calc(100vw-32px)] flex justify-center items-center border border-paper-border paper-shadow p-3 bg-white rounded-lg shadow relative group overflow-hidden gap-2.5"
       @click="togglePauseTimer(toast.id)">

    <template v-if="!toast.avatar">
      <bf-badge v-if="toast.type === 'custom'"
                size="sm"
                :color="toast.iconColor"
                square>
        <icon-bounding-box :bounding-box="20" :icon="computedIcon"/>
      </bf-badge>
      <bf-badge v-else-if="toast.type === 'success'"
                size="sm"
                color="green"
                square>
        <icon-bounding-box :bounding-box="20" :icon="['far', 'check']"/>
      </bf-badge>
      <bf-badge v-else-if="toast.type === 'danger'"
                size="sm"
                color="red"
                square>
        <icon-bounding-box :bounding-box="20" :icon="['far', 'triangle-exclamation']"/>
      </bf-badge>
      <bf-badge v-else-if="toast.type === 'warning'"
                size="sm"
                color="yellow"
                square>
        <icon-bounding-box :bounding-box="20" :icon="['far', 'triangle-exclamation']"/>
      </bf-badge>
      <bf-badge v-else
                size="sm"
                color="gray"
                square>
        <icon-bounding-box :bounding-box="20" :icon="['far', 'circle-info']"/>
      </bf-badge>
    </template>
    <template v-else-if="toast.avatar">
      <bf-avatar :icon="toast.avatar" border size="sm"/>
    </template>
    <div class="flex w-full justify-between items-center">
      <div class="inline-flex flex-col gap-4 overflow-hidden">
        <div class="inline-flex flex-col flex-shrink">
          <h3 class="leading-none text-sm font-semibold truncate">{{ toast.title }}</h3>
          <template v-if="toast.html">
            <span v-html="toast.message"/>
          </template>
          <div v-else class="text-base font-normal">
            {{ toast.message }}
          </div>
          <div v-if="toast.showTimestamp" class="text-xs font-normal text-button-link-label">
            {{ toastTimestamp }}
          </div>
        </div>

        <div v-if="toast.button">
          <bf-button color="outline" class="inline-flex" @click.stop="handleButtonClick">
            {{ toast.button.text }}
          </bf-button>
        </div>

        <div v-if="toast.validations"
             ref="validationsContainer"
             class="inline-flex self-start flex-col gap-1"
             :style="validationsMaxWidth">
          <bf-badge v-for="validation in toast.validations"
                    :key="validation.text"
                    v-tooltip-on-truncated
                    no-wrap
                    class="truncate self-start"
                    :icon="getIconMethod(validation.icon) ?? ['far', 'triangle-exclamation']"
                    size="xs"
                    :outlined="validation.outlined"
                    :color="validation.color">
            {{ validation.text }}
          </bf-badge>
        </div>
      </div>
      <div class="flex items-center justify-center">
        <button v-if="toast.closable"
                type="button"
                class="ms-auto -mx-1.5 p-1.5  -my-1.5 bg-white text-text-primary hover:text-text-primary rounded-lg focus:ring-2 focus:ring-gray-300 hover:bg-gray-100 inline-flex items-center justify-center h-8 w-8"
                aria-label="Close"
                @click.stop="removeToast(toast.id, true)">
          <icon-bounding-box :icon="['far', 'xmark']" :bounding-box="20"/>
        </button>
      </div>
    </div>

    <div v-if="toast.duration > 0"
         class="absolute bottom-0 left-0 h-1 bg-blue-900 w-full animate-progress group-hover:[animation-play-state:paused]"
         :style="{ animationDuration: toast.duration + 'ms', animationPlayState: isPaused ? 'paused' : 'running' }"/>
  </div>
</template>

<script setup lang="ts">
import type {Toast} from '~/store/toast';
import type {PropType} from 'vue';
import {useI18n} from 'vue-i18n';
import {useIconProp} from '~/components/global/utils/iconPropUtil';
import BfAvatar from '~/components/global/BfAvatar.vue';

const props = defineProps({
  toast: {
    type: Object as PropType<Toast>,
    required: true
  }
});

const {t} = useI18n();
const emit = defineEmits(['remove-toast', 'pause-timeout', 'resume-timeout']);
const {$dayjs} = useNuxtApp();
const {computedIcon, getIconMethod} = useIconProp({icon: props.toast.icon});
const validationsContainer = ref<HTMLElement | null>(null);

const toastTimestamp = computed(() => {
  const timestamp = $dayjs(props.toast.timestamp);

  if (!timestamp.isValid()) return '';

  // Determine the "humanized" part based on the day difference
  const humanizedPart = timestamp.isToday()
    ? t('dates.today') // Localized "Today" if supported
    : $dayjs().to(timestamp, true);

  // Determine the time part
  const timePart = timestamp.isToday()
    ? timestamp.fromNow() // Includes "ago" for today timestamps
    : timestamp.format('HH:mm'); // Exact time for non-today timestamps

  return `${humanizedPart} · ${timePart}`;
});

const validationsMaxWidth = computed(() => {
  if (!validationsContainer.value) return {
    maxWidth: '356px'
  };

  const containerMaxWidth = validationsContainer.value.clientWidth;

  if (containerMaxWidth > 365) {
    return {
      maxWidth: `${containerMaxWidth}px`
    };
  } else {
    return {
      maxWidth: '356px'
    };
  }
});

const removeToast = (id: string, markAsRead: boolean = false) => {
  emit('remove-toast', id, markAsRead);
};

const pauseTimeout = (id: string) => {
  emit('pause-timeout', id);
};

const resumeTimeout = (id: string) => {
  emit('resume-timeout', id);
};

const isPaused = ref(false);

// Toggle pause/resume on toast click
const togglePauseTimer = (id: string) => {
  if (isPaused.value) {
    isPaused.value = false;
    resumeTimeout(id);
  } else {
    isPaused.value = true;
    pauseTimeout(id);
  }
};

const handleButtonClick = () => {
  if (typeof props.toast?.button?.callback === 'function') {
    props.toast?.button.callback();
    removeToast(props.toast.id, true);
  }
};

</script>